import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { FormAnswerValue } from '../../forms/form-types';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { FormVersionEntity } from './form-version.entity';
import { FormEntity } from './form.entity';

/** PD-13768 — where a sent form stands. Expired is worked out from the link's expiry. */
export type SentFormStatus =
	| 'sent'
	| 'opened'
	| 'completed'
	| 'cancelled'
	| 'expired';

export type SentFormChannel = 'email' | 'sms';

/** One entry of a sent form's log: each send, resend, open, completion and cancel. */
export interface SentFormLogEntryModel {
	/** Server time, ISO 8601. */
	at: string;
	event: 'sent' | 'resent' | 'opened' | 'completed' | 'cancelled' | 'send_failed';
	channel?: SentFormChannel | null;
	/** The email address or phone number it went to. */
	to?: string | null;
	/** Tenant user id of the staff member who did it; null for the customer. */
	userId?: number | null;
	detail?: string | null;
}

/**
 * One proposed change from a customer's answers to the client record (sent-forms.ts):
 * the target, the value on file when the form was sent, the customer's value, and staff's
 * decision when the submission is processed.
 */
export interface SentFormProposedChangeModel {
	fieldKey: string;
	label: string;
	target: string;
	from: string;
	to: string;
	decision: 'accepted' | 'rejected' | null;
	decidedAt: string | null;
	decidedBy: number | null;
}

/**
 * What processing a sent form's submission has done, per post-action: when, who, and the
 * orders it created. Read before an action runs, under a row lock, so processing twice
 * never creates the same orders twice.
 */
export interface SentFormProcessedActionModel {
	at: string;
	userId: number | null;
	orderIds?: Array<number>;
	detail?: string | null;
}

/**
 * PD-13768 — a form sent to a known client to complete and sign through a no-login link
 * (docs/claude/forms/04-sent-forms.md).
 *
 * The form version is fixed when it is sent, so the customer fills exactly what the
 * sender previewed. `prefilledValues` are the values read from the client when it was
 * sent (what proposed changes are measured against); `staffValues` are what the sender
 * typed. The link itself is a `payment_link` row of type `form` (`fk_payment_link__id`):
 * its token is stored only as a keyed hash; a resend retires it and issues a new one.
 */
@Index('idx_sent_form_client', ['fkClientId'], {})
@Index('idx_sent_form_status', ['status'], {})
@Index('idx_sent_form_payment_link', ['fkPaymentLinkId'], {})
@Entity('sent_form')
export class SentFormEntity extends BaseEntity {
	@Column('int', { name: 'fk_form__id', unsigned: true })
	public fkFormId!: number;

	@Column('int', { name: 'fk_form_version__id', unsigned: true })
	public fkFormVersionId!: number;

	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	// The contact it was sent to, when the recipient is one of the client's contacts.
	@Column('int', { name: 'fk_client_contact__id', nullable: true, unsigned: true })
	public fkClientContactId!: number | null;

	// The live link. A resend points this at the new one.
	@Column('int', { name: 'fk_payment_link__id', nullable: true, unsigned: true })
	public fkPaymentLinkId!: number | null;

	@Column('varchar', { name: 'channel', length: 10 })
	public channel!: SentFormChannel;

	// The address or number it was last sent to.
	@Column('varchar', { name: 'sentTo', length: 255 })
	public sentTo!: string;

	@Column('varchar', { name: 'recipientName', nullable: true, length: 255 })
	public recipientName!: string | null;

	// Tenant user id of the sender.
	@Column('int', { name: 'fk_user__id_sender', nullable: true, unsigned: true })
	public fkUserIdSender!: number | null;

	@Column('json', { name: 'prefilledValues' })
	public prefilledValues!: Record<string, FormAnswerValue>;

	@Column('json', { name: 'staffValues' })
	public staffValues!: Record<string, FormAnswerValue>;

	// Choices the sender made that are not answers: whether a payment method is still
	// required when the terms are Bill To.
	@Column('json', { name: 'sendOptions', nullable: true })
	public sendOptions!: { paymentMethodRequired?: boolean } | null;

	@Column('varchar', { name: 'status', length: 20, default: 'sent' })
	public status!: SentFormStatus;

	@Column('datetime', { name: 'expiresAt' })
	public expiresAt!: Date;

	@Column('datetime', { name: 'openedAt', nullable: true })
	public openedAt!: Date | null;

	@Column('datetime', { name: 'completedAt', nullable: true })
	public completedAt!: Date | null;

	@Column('datetime', { name: 'cancelledAt', nullable: true })
	public cancelledAt!: Date | null;

	@Column('int', { name: 'fk_user__id_canceller', nullable: true, unsigned: true })
	public fkUserIdCanceller!: number | null;

	@Column('int', { name: 'fk_form_submission__id', nullable: true, unsigned: true })
	public fkFormSubmissionId!: number | null;

	// The signed PDF in the client's Files (`file._id`), replaced when countersigned.
	@Column('int', { name: 'fk_file__id_signed', nullable: true, unsigned: true })
	public fkFileIdSigned!: number | null;

	@Column('json', { name: 'proposedChanges', nullable: true })
	public proposedChanges!: Array<SentFormProposedChangeModel> | null;

	// Processing post-actions done, keyed by action (sent-form processing).
	@Column('json', { name: 'processedActions', nullable: true })
	public processedActions!: Record<string, SentFormProcessedActionModel> | null;

	@Column('json', { name: 'log', nullable: true })
	public log!: Array<SentFormLogEntryModel> | null;

	@ManyToOne(() => FormEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_form__id', referencedColumnName: 'id' }])
	public form!: FormEntity;

	@ManyToOne(() => FormVersionEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_form_version__id', referencedColumnName: 'id' }])
	public formVersion!: FormVersionEntity;

	@ManyToOne(() => ClientEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public client!: ClientEntity;
}
